/**
 * GestureHandler — Toma gestos reconocidos por GestureRecognizer y los
 * traduce en efectos clínicos (palpación/auscultación/percusión revelan
 * hallazgos via GestureSemantics) o de control (emergencia, time scale).
 *
 * Separado de main.ts para que el enum MedicalGesture tenga un único lugar
 * donde se decide qué hacer con cada gesto.
 */

import * as THREE from 'three';
import state from '../core/state';
import { GestureRecognizer, MedicalGesture } from './GestureRecognizer';
import { Patient } from '../medical/Patient';
import { GestureSemantics } from '../medical/GestureSemantics';
import { SceneManager } from '../visualization/SceneManager';

type ClinicalGesture = 'palpation' | 'percussion' | 'auscultation';

export class GestureHandler {
  private patient: Patient | null = null;

  private prevZoomDistance = 0;

  // HUD que se pinta durante un gesture hold (emergency/auscultation). Sin
  // este feedback, el alumno mantiene la postura "a ciegas" durante 1.5-2s
  // sin saber que algo se está activando.
  private holdHud: HTMLElement | null = null;

  constructor(
    private recognizer: GestureRecognizer,
    private semantics: GestureSemantics,
    private sceneManager: SceneManager | null = null
  ) {}

  setSceneManager(sm: SceneManager | null): void {
    this.sceneManager = sm;
  }

  setPatient(patient: Patient | null): void {
    this.patient = patient;
    // Al cambiar de caso, cualquier hold en curso (emergency/auscultation)
    // y el historial de gestos del caso anterior ya no son válidos.
    this.recognizer.reset();
    this.prevZoomDistance = 0;
    this.hideHoldHud();
  }

  /** Llamar cada frame después de actualizar el estado de las manos. */
  tick(): void {
    const activeHand = state.getActiveHand();
    if (!activeHand) {
      this.hideHoldHud();
      return;
    }

    const [rightHand, leftHand] = state.getBothHands();
    const otherHand = rightHand && leftHand
      ? (activeHand === rightHand ? leftHand : rightHand)
      : undefined;

    // Gate para AUSCULTATION: sólo medimos el hold si hay un nodo bajo la
    // mano. HandInteractionController actualiza hoveredNodeId cada frame
    // via raycast 2D. Sin este gate, una mano abierta casual disparaba el
    // HUD todo el tiempo.
    this.recognizer.hasHoverTarget = state.interaction.hoveredNodeId !== null;

    const result = this.recognizer.recognize(activeHand, undefined, otherHand ?? undefined);

    if (result.gesture !== MedicalGesture.NONE) {
      this.execute(result.gesture, result.data);
    }

    // Feedback visual de holds en curso. Prioriza emergency (más destructivo).
    const emergencyProgress = this.recognizer.getEmergencyHoldProgress();
    const auscultationProgress = this.recognizer.getAuscultationHoldProgress();
    if (emergencyProgress !== null && emergencyProgress < 1) {
      this.showHoldHud('emergency', emergencyProgress);
    } else if (auscultationProgress !== null && auscultationProgress < 1) {
      this.showHoldHud('auscultation', auscultationProgress);
    } else {
      this.hideHoldHud();
    }
  }

  private execute(gesture: MedicalGesture, data?: Record<string, unknown>): void {
    switch (gesture) {
      case MedicalGesture.PALPATION:
        this.applyClinical('palpation');
        break;
      case MedicalGesture.PERCUSSION:
        this.applyClinical('percussion');
        break;
      case MedicalGesture.AUSCULTATION:
        this.applyClinical('auscultation');
        break;
      case MedicalGesture.EMERGENCY_CLEAR:
        state.toggleEmergencyMode();
        break;
      case MedicalGesture.TIME_DILATION: {
        const dist = data?.distance;
        if (typeof dist === 'number') {
          state.setTimeScale(Math.max(1, dist * 10));
        }
        break;
      }
      case MedicalGesture.ZOOM_FOCUS: {
        // Zoom con dos manos lo maneja HandInteractionController.handleTwoHandsZoom
        // usando los indexTipScreen (coords 2D). Ese path es más fiable que
        // el Z de MediaPipe, así que acá lo ignoramos para no pelear contra él.
        break;
      }
      case MedicalGesture.TEMPORAL_SCROLL: {
        // Swipe horizontal de mano cambiaba el reloj clínico, pero se
        // disparaba con movimientos naturales (alumno apuntando, moviendo
        // la mano al cruzar) y generaba saltos de tiempo sin querer.
        // El control de tiempo vive en el sidebar (slider/botones explícitos).
        break;
      }
      // ANATOMICAL_CUT, DIFFERENTIAL_PULL, LAYER_SWITCH: declarados en el enum
      // pero el recognizer nunca los emite. Se dejan como stubs semánticos
      // para cuando se implementen los detectores correspondientes.
      default:
        break;
    }
  }

  /**
   * ZOOM_FOCUS emite la distancia actual entre las dos manos en pinch.
   * Comparamos con el frame anterior para derivar dirección del zoom:
   * manos separándose → zoom out; juntándose → zoom in.
   */
  private applyZoom(currentDistance: number): void {
    if (!this.sceneManager) return;
    if (this.prevZoomDistance === 0) {
      this.prevZoomDistance = currentDistance;
      return;
    }
    const delta = currentDistance - this.prevZoomDistance;
    this.prevZoomDistance = currentDistance;
    if (Math.abs(delta) < 0.01) return;

    const cam = this.sceneManager.camera;
    const forward = cam.getWorldDirection(new THREE.Vector3());
    // delta>0 (separan) → alejar cámara (zoom out). delta<0 → acercar.
    cam.position.addScaledVector(forward, -delta * 4);
  }

  /**
   * Aplica un gesto clínico sobre el nodo bajo el cursor/seleccionado.
   * Responsabilidad SOLO semántica: GestureSemantics decide si el gesto
   * aporta un hallazgo clínico según el sistema anatómico.
   *
   * Feedback visual (pulse + expand + setSelectedNode) NO se hace acá:
   * HandInteractionController.handlePinch ya los dispara cuando el pinch
   * comienza. Si duplicáramos, se ve doble animación y compite por el
   * selectedNodeId.
   */
  private applyClinical(gesture: ClinicalGesture): void {
    const targetId = state.interaction.hoveredNodeId ?? state.interaction.selectedNodeId;
    if (!targetId || !this.patient) return;
    this.semantics.apply(gesture, targetId);
  }

  // ────────────────────────────────────────────────────────────────────
  // HUD de feedback de hold (E)
  // ────────────────────────────────────────────────────────────────────

  private ensureHoldHud(): HTMLElement {
    if (this.holdHud) return this.holdHud;
    this.injectHoldHudStyles();
    const el = document.createElement('div');
    el.id = 'cgt-hold-hud';
    el.innerHTML = `
      <div class="cgt-hold-label"></div>
      <div class="cgt-hold-bar"><div class="cgt-hold-bar-fill"></div></div>
      <div class="cgt-hold-hint">Mantené la postura…</div>
    `;
    document.body.appendChild(el);
    this.holdHud = el;
    return el;
  }

  private showHoldHud(type: 'emergency' | 'auscultation', progress: number): void {
    const el = this.ensureHoldHud();
    const labelEl = el.querySelector<HTMLElement>('.cgt-hold-label')!;
    const fillEl = el.querySelector<HTMLElement>('.cgt-hold-bar-fill')!;
    const isEmergency = type === 'emergency';
    labelEl.textContent = isEmergency ? '🚨 Activando modo emergencia' : '🩺 Auscultando…';
    el.classList.toggle('cgt-hold-emergency', isEmergency);
    fillEl.style.width = `${Math.round(progress * 100)}%`;
    el.style.opacity = '1';
    el.style.pointerEvents = 'none';
  }

  private hideHoldHud(): void {
    if (this.holdHud) this.holdHud.style.opacity = '0';
  }

  private injectHoldHudStyles(): void {
    if (document.getElementById('cgt-hold-hud-styles')) return;
    const style = document.createElement('style');
    style.id = 'cgt-hold-hud-styles';
    style.textContent = `
      #cgt-hold-hud {
        position: fixed; top: 80px; left: 50%;
        transform: translateX(-50%);
        z-index: 800;
        min-width: 240px;
        padding: 10px 16px;
        background: rgba(10,10,10,0.85); backdrop-filter: blur(12px);
        border: 0.5px solid rgba(255,255,255,0.1); border-radius: 12px;
        color: #f5f5f7;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        opacity: 0;
        transition: opacity 0.15s ease;
        pointer-events: none;
      }
      #cgt-hold-hud.cgt-hold-emergency {
        border-color: rgba(255,69,58,0.4);
        box-shadow: 0 0 24px rgba(255,69,58,0.25);
      }
      #cgt-hold-hud .cgt-hold-label {
        font-size: 12px; font-weight: 600; margin-bottom: 6px;
        text-align: center;
      }
      #cgt-hold-hud .cgt-hold-bar {
        height: 4px; background: rgba(255,255,255,0.06);
        border-radius: 2px; overflow: hidden;
      }
      #cgt-hold-hud .cgt-hold-bar-fill {
        height: 100%; background: #30D158;
        border-radius: 2px; transition: width 0.08s linear;
      }
      #cgt-hold-hud.cgt-hold-emergency .cgt-hold-bar-fill { background: #FF453A; }
      #cgt-hold-hud .cgt-hold-hint {
        font-size: 10px; color: #8e8e93; margin-top: 5px; text-align: center;
      }
    `;
    document.head.appendChild(style);
  }
}

export default GestureHandler;
